import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  TrendingUp, 
  Lightbulb, 
  Activity, 
  Plus, 
  Loader2, 
  Sparkles, 
  CheckCircle2 
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface StructuredRecord {
  type: string;
  test_name?: string;
  measurement_type?: string;
  measurement_type_slug?: string;
  structure_type?: string;
  is_main_record?: boolean;
  value?: any;
  [key: string]: any;
}

interface CategorizationSuggestionsProps {
  records: StructuredRecord[];
  patientId: string;
  sessionId: string;
  onSubcategoriesCreated: () => Promise<void>;
}

interface SuggestedTest {
  name: string;
  count: number;
}

export function CategorizationSuggestions({
  records,
  patientId,
  sessionId,
  onSubcategoriesCreated,
}: CategorizationSuggestionsProps) {
  const [suggestedTests, setSuggestedTests] = useState<SuggestedTest[]>([]);
  const [creating, setCreating] = useState(false);
  const [creatingAll, setCreatingAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  const detectNewTestNames = useCallback(async () => {
    try {
      setLoading(true);

      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;
      
      setUserId(userData.user.id);

      // Helper para mapear measurement_type de anthropometry para test_name
      const mapAnthropometryToTestName = (measurementType: string | null | undefined): string | null => {
        if (!measurementType) return null;
        // ✅ Normalizar: remover espaços, acentos e converter para lowercase
        const normalized = measurementType
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_') // Substituir espaços por underscore
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, ''); // Remover acentos
        
        const mapping: Record<string, string> = {
          'peso': 'Peso',
          'weight': 'Peso',
          'imc': 'IMC',
          'bmi': 'IMC',
          'altura': 'Altura',
          'height': 'Altura',
          'circunferencia_cintura': 'Circunferência da Cintura',
          'circunferenciacintura': 'Circunferência da Cintura',
          'circunferencia_da_cintura': 'Circunferência da Cintura',
          'cintura': 'Circunferência da Cintura',
          'waist': 'Circunferência da Cintura',
          'circunferencia_quadril': 'Circunferência do Quadril',
          'circunferenciaquadril': 'Circunferência do Quadril',
          'circunferencia_do_quadril': 'Circunferência do Quadril',
          'quadril': 'Circunferência do Quadril',
          'hip': 'Circunferência do Quadril',
          'gordura_corporal': 'Gordura Corporal',
          'gorduracorporal': 'Gordura Corporal',
          'body_fat': 'Gordura Corporal',
          'massa_magra': 'Massa Magra',
          'massamagra': 'Massa Magra',
          'lean_mass': 'Massa Magra',
          'rcq': 'RCQ',
          'waist_hip_ratio': 'RCQ',
          'whtr': 'RCQ'
        };
        return mapping[normalized] || measurementType.charAt(0).toUpperCase() + measurementType.slice(1).toLowerCase();
      };

      // Extract test_names from records (lab_result, vital_sign, anthropometry, e physiotherapy_evolution com antropometry)
      const testNames = records
        .filter(r => {
          if (r.type === 'lab_result' || r.type === 'vital_sign') {
            return r.test_name && r.test_name.trim();
          }
          // ✅ Antropometria direta - aceitar se tiver measurement_type_slug OU measurement_type E value
          if (r.type === 'anthropometry' && !r.is_main_record) {
            return (r.measurement_type_slug || r.measurement_type) && r.value != null;
          }
          // ✅ Antropometria embutida em physiotherapy_evolution - aceitar se tiver measurement_type_slug OU measurement_type E value
          if (r.type === 'physiotherapy_evolution' && 
              r.structure_type === 'antropometry' && 
              !r.is_main_record) {
            return (r.measurement_type_slug || r.measurement_type) && r.value != null;
          }
          return false;
        })
        .map(r => {
          // Mapear anthropometry ou physiotherapy_evolution com structure_type='antropometry'
          // Usar measurement_type (texto de exibição) para mapear para test_name
          if (r.type === 'anthropometry' || 
              (r.type === 'physiotherapy_evolution' && r.structure_type === 'antropometry')) {
            // ✅ Priorizar measurement_type (mais legível), senão usar measurement_type_slug como fallback
            const measurementType = r.measurement_type || r.measurement_type_slug;
            if (!measurementType) {
              console.warn('[CategorizationSuggestions] Registro de antropometria sem measurement_type ou measurement_type_slug:', r);
              return null;
            }
            const mappedName = mapAnthropometryToTestName(measurementType);
            console.log('[CategorizationSuggestions] Mapeando antropometria:', {
              type: r.type,
              structure_type: r.structure_type,
              measurement_type: r.measurement_type,
              measurement_type_slug: r.measurement_type_slug,
              mappedName
            });
            return mappedName;
          }
          return r.test_name?.trim();
        })
        .filter((name): name is string => !!name);

      console.log('[CategorizationSuggestions] Test names detectados:', testNames);

      if (testNames.length === 0) {
        setSuggestedTests([]);
        setLoading(false);
        return;
      }

      // Count occurrences
      const testNameCounts: Record<string, { original: string; count: number }> = testNames.reduce((acc, name) => {
        const normalized = name.toLowerCase().trim();
        acc[normalized] = {
          original: name,
          count: (acc[normalized]?.count || 0) + 1
        };
        return acc;
      }, {} as Record<string, { original: string; count: number }>);

      // Get existing subcategories for the user
      const { data: existingSubcategories } = await supabase
        .from('lab_subcategories')
        .select('name')
        .eq('user_id', userData.user.id);

      const existingNames = new Set(
        (existingSubcategories || []).map(s => s.name.toLowerCase().trim())
      );

      // ✅ NOVO: Verificar também se já existem registros em lab_results_onboarding com subcategory_id
      // Isso é importante porque os registros podem já ter sido vinculados automaticamente pelo trigger SQL
      const { data: existingLabResults } = await supabase
        .from('lab_results_onboarding')
        .select('test_name, subcategory_id, source_type')
        .eq('patient_id', patientId)
        .not('subcategory_id', 'is', null);

      console.log('[CategorizationSuggestions] Registros existentes em lab_results_onboarding com subcategory_id:', existingLabResults);

      const existingLabResultNames = new Set(
        (existingLabResults || [])
          .map(r => r.test_name?.toLowerCase().trim())
          .filter((name): name is string => !!name)
      );

      console.log('[CategorizationSuggestions] Nomes de test_name já vinculados:', Array.from(existingLabResultNames));
      console.log('[CategorizationSuggestions] Nomes de subcategorias existentes:', Array.from(existingNames));

      // Filter out test_names that already have a subcategory OR already have lab_results with subcategory_id
      const newTests: SuggestedTest[] = Object.entries(testNameCounts)
        .filter(([normalizedName, value]) => {
          // Verificar se já existe subcategoria com esse nome
          if (existingNames.has(normalizedName)) {
            console.log(`[CategorizationSuggestions] Filtrando "${value.original}" - já existe subcategoria`);
            return false;
          }
          // Verificar se já existem lab_results com subcategory_id para esse test_name
          if (existingLabResultNames.has(normalizedName)) {
            console.log(`[CategorizationSuggestions] Filtrando "${value.original}" - já existe lab_result com subcategory_id`);
            return false;
          }
          console.log(`[CategorizationSuggestions] Mantendo "${value.original}" como sugestão`);
          return true;
        })
        .map(([_, value]) => ({
          name: value.original,
          count: value.count
        }))
        .sort((a, b) => b.count - a.count);

      console.log('[CategorizationSuggestions] Sugestões finais:', newTests);

      setSuggestedTests(newTests);
    } catch (error) {
      console.error('Error detecting test names:', error);
      toast.error('Erro ao detectar novas medidas');
    } finally {
      setLoading(false);
    }
  }, [records, patientId]);

  useEffect(() => {
    detectNewTestNames();
  }, [detectNewTestNames]);

  const handleCreateSubcategory = async (testName: string) => {
    if (!userId) return;

    try {
      setCreating(true);

      // ✅ Verificar se é uma medida de antropometria
      // Lista de medidas antropométricas conhecidas
      const anthropometryMeasures = [
        'Peso', 'Altura', 'IMC', 'Circunferência da Cintura', 
        'Circunferência do Quadril', 'Gordura Corporal', 'Massa Magra', 'RCQ'
      ];
      
      // Verificar também pelo source_type no banco de dados
      const { data: existingResults } = await supabase
        .from('lab_results_onboarding')
        .select('source_type')
        .eq('patient_id', patientId)
        .eq('test_name', testName)
        .limit(1)
        .maybeSingle();

      const isAnthropometry = anthropometryMeasures.includes(testName) || 
                              existingResults?.source_type === 'anthropometry';

      // ✅ Determinar categoria baseado no tipo de medida
      // Categorias fixas: Dados do paciente, Exames, Antropometria, Alimentação
      // Para antropometria: usar "Antropometria" ou "Dados do paciente"
      // Para exames: usar "Exames" ou "Dados do paciente"
      let categoryName: string;
      if (isAnthropometry) {
        // Verificar se já existe em "Dados do paciente" (compartilhado)
        const { data: existingInDados } = await supabase
          .from('lab_subcategories')
          .select('id, category:lab_categories!inner(name)')
          .eq('user_id', userId)
          .eq('name', testName)
          .eq('lab_categories.name', 'Dados do paciente')
          .maybeSingle();
        
        categoryName = existingInDados ? 'Dados do paciente' : 'Antropometria';
      } else {
        // Para exames, verificar se já existe em "Dados do paciente"
        const { data: existingInDados } = await supabase
          .from('lab_subcategories')
          .select('id, category:lab_categories!inner(name)')
          .eq('user_id', userId)
          .eq('name', testName)
          .eq('lab_categories.name', 'Dados do paciente')
          .maybeSingle();
        
        categoryName = existingInDados ? 'Dados do paciente' : 'Exames';
      }

      // ✅ Buscar categoria fixa (não criar)
      const { data: targetCategory, error: categoryError } = await supabase
        .from('lab_categories')
        .select('id')
        .eq('user_id', userId)
        .eq('name', categoryName)
        .eq('is_system', true)
        .maybeSingle();

      if (categoryError) throw categoryError;
      
      let finalTargetCategory = targetCategory;
      
      if (!finalTargetCategory) {
        // Se a categoria fixa não existir, criar manualmente
        // Isso pode acontecer se o usuário ainda não passou pelo onboarding
        const displayOrder = categoryName === 'Dados do paciente' ? 1 :
                            categoryName === 'Exames' ? 2 :
                            categoryName === 'Antropometria' ? 3 : 4;
        
        const { data: newCategory, error: newCategoryError } = await supabase
          .from('lab_categories')
          .insert({
            user_id: userId,
            name: categoryName,
            display_order: displayOrder,
            is_system: true
          })
          .select()
          .single();

        if (newCategoryError) {
          // Se der erro de duplicação, tentar buscar novamente
          const { data: retryCategory } = await supabase
            .from('lab_categories')
            .select('id')
            .eq('user_id', userId)
            .eq('name', categoryName)
            .maybeSingle();
          
          if (!retryCategory) {
            throw new Error(`Erro ao criar/buscar categoria fixa "${categoryName}": ${newCategoryError.message}`);
          }
          finalTargetCategory = retryCategory;
        } else {
          finalTargetCategory = newCategory;
        }
      }

      // Create new subcategory
      if (!finalTargetCategory) {
        throw new Error(`Categoria "${categoryName}" não encontrada e não foi possível criar`);
      }

      const { data: newSubcategory, error: subcategoryError } = await supabase
        .from('lab_subcategories')
        .insert({
          category_id: finalTargetCategory.id,
          user_id: userId,
          name: testName,
          show_chart: true,
          is_auto_created: true,
          slug: '', // Trigger auto-gera do name
        })
        .select()
        .single();

      if (subcategoryError) throw subcategoryError;

      // Link existing lab results to the new subcategory
      // ✅ Para antropometria, também atualizar registros com source_type='anthropometry'
      if (isAnthropometry) {
        const { error: updateError } = await supabase
          .from('lab_results_onboarding')
          .update({ subcategory_id: newSubcategory.id })
          .eq('patient_id', patientId)
          .eq('test_name', testName)
          .eq('source_type', 'anthropometry')
          .is('subcategory_id', null);

        if (updateError) throw updateError;
      } else {
        // Para lab_exam, atualizar normalmente
        const { error: updateError } = await supabase
          .from('lab_results_onboarding')
          .update({ subcategory_id: newSubcategory.id })
          .eq('patient_id', patientId)
          .eq('test_name', testName)
          .is('subcategory_id', null);

        if (updateError) throw updateError;
      }

      // ✅ NOVO: Também atualizar structured_records da sessão para anthropometry
      // Buscar sessão atual
      const { data: session, error: sessionError } = await supabase
        .from('smartfile_sessions')
        .select('structured_records')
        .eq('id', sessionId)
        .single();

      if (!sessionError && session?.structured_records && Array.isArray(session.structured_records)) {
        // Helper para mapear test_name de volta para measurement_type_slug
        const mapTestNameToMeasurementTypeSlug = (testName: string): string[] => {
          const mapping: Record<string, string[]> = {
            'Peso': ['peso', 'weight'],
            'IMC': ['imc', 'bmi'],
            'Altura': ['altura', 'height'],
            'Circunferência da Cintura': ['circunferencia_cintura', 'circunferencia_cintura', 'circunferenciacintura', 'cintura', 'waist'],
            'Circunferência do Quadril': ['circunferencia_quadril', 'circunferencia_quadril', 'circunferenciaquadril', 'quadril', 'hip'],
            'Gordura Corporal': ['gordura_corporal', 'gordura_corporal', 'gorduracorporal', 'body_fat'],
            'Massa Magra': ['massa_magra', 'massa_magra', 'massamagra', 'lean_mass'],
            'RCQ': ['rcq', 'waist_hip_ratio', 'whtr']
          };
          return mapping[testName] || [testName.toLowerCase().replace(/\s+/g, '_').normalize('NFD').replace(/[\u0300-\u036f]/g, '')];
        };

        const measurementTypeSlugs = mapTestNameToMeasurementTypeSlug(testName);
        let updated = false;
        const updatedRecords = (session.structured_records as StructuredRecord[]).map((record) => {
          // Verificar tanto anthropometry quanto physiotherapy_evolution com structure_type='antropometry'
          // Usar measurement_type_slug para matching (padronizado como lab_result usa slug)
          if (
            (record?.type === 'anthropometry' || 
             (record?.type === 'physiotherapy_evolution' && record?.structure_type === 'antropometry')) &&
            record?.measurement_type_slug &&
            measurementTypeSlugs.includes(record.measurement_type_slug.toLowerCase())
          ) {
            updated = true;
            return {
              ...record,
              subcategory_id: newSubcategory.id
            };
          }
          return record;
        });

        if (updated) {
          const { error: updateStructuredError } = await supabase
            .from('smartfile_sessions')
            .update({ structured_records: updatedRecords as any as typeof session.structured_records })
            .eq('id', sessionId);

          if (updateStructuredError) {
            console.error('Error updating structured_records:', updateStructuredError);
            // Não falhar o fluxo se isso der erro, apenas logar
          }
        }
      }

      toast.success(`✅ Subcategoria "${testName}" criada e vinculada!`);

      // Remove from suggestions
      setSuggestedTests(prev => prev.filter(t => t.name !== testName));

      // Callback to reload session data
      await onSubcategoriesCreated();
      
      // Disparar evento para recarregar gráficos nos pacientes
      window.dispatchEvent(new CustomEvent('reload-health-indicators'));

    } catch (error) {
      console.error('Error creating subcategory:', error);
      toast.error('Erro ao criar subcategoria');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateAllSubcategories = async () => {
    if (!userId) return;

    setCreatingAll(true);

    try {
      // ✅ PASSO 1: Determinar categorias para cada teste
      const testCategories: Array<{ testName: string; categoryName: string; isAnthropometry: boolean }> = [];
      
      for (const test of suggestedTests) {
        // Verificar se é uma medida de antropometria
        const anthropometryMeasures = [
          'Peso', 'Altura', 'IMC', 'Circunferência da Cintura', 
          'Circunferência do Quadril', 'Gordura Corporal', 'Massa Magra', 'RCQ'
        ];
        
        const { data: existingResults } = await supabase
          .from('lab_results_onboarding')
          .select('source_type')
          .eq('patient_id', patientId)
          .eq('test_name', test.name)
          .limit(1)
          .maybeSingle();

        const isAnthropometry = anthropometryMeasures.includes(test.name) || 
                                existingResults?.source_type === 'anthropometry';

        let categoryName: string;
        if (isAnthropometry) {
          const { data: existingInDados } = await supabase
            .from('lab_subcategories')
            .select('id, category:lab_categories!inner(name)')
            .eq('user_id', userId)
            .eq('name', test.name)
            .eq('lab_categories.name', 'Dados do paciente')
            .maybeSingle();
          
          categoryName = existingInDados ? 'Dados do paciente' : 'Antropometria';
        } else {
          const { data: existingInDados } = await supabase
            .from('lab_subcategories')
            .select('id, category:lab_categories!inner(name)')
            .eq('user_id', userId)
            .eq('name', test.name)
            .eq('lab_categories.name', 'Dados do paciente')
            .maybeSingle();
          
          categoryName = existingInDados ? 'Dados do paciente' : 'Exames';
        }

        testCategories.push({ testName: test.name, categoryName, isAnthropometry });
      }

      // ✅ PASSO 2: Buscar ou criar categorias necessárias
      const uniqueCategories = [...new Set(testCategories.map(tc => tc.categoryName))];
      const categoryMap = new Map<string, string>();

      for (const categoryName of uniqueCategories) {
        let category;
        // ✅ CORRIGIDO: Buscar categoria sem filtrar por is_system para evitar duplicatas
        // A constraint UNIQUE (user_id, name) garante que só existe uma categoria com esse nome
        const { data: categoryData, error: categoryError } = await supabase
          .from('lab_categories')
          .select('id, is_system')
          .eq('user_id', userId)
          .eq('name', categoryName)
          .maybeSingle();

        if (categoryError) throw categoryError;
        category = categoryData;

        if (!category) {
          // Se não existe, criar a categoria
          // A constraint UNIQUE (user_id, name) vai prevenir duplicatas mesmo se chamado múltiplas vezes
          const displayOrder = categoryName === 'Dados do paciente' ? 1 :
                              categoryName === 'Exames' ? 2 :
                              categoryName === 'Antropometria' ? 3 : 4;
          
          const { data: newCategory, error: newCategoryError } = await supabase
            .from('lab_categories')
            .insert({
              user_id: userId,
              name: categoryName,
              display_order: displayOrder,
              is_system: true
            })
            .select('id')
            .single();

          if (newCategoryError) {
            // Se der erro de duplicação (constraint violation 23505), buscar novamente
            // Isso pode acontecer se outra requisição criou a categoria simultaneamente
            if (newCategoryError.code === '23505') {
              const { data: retryCategory } = await supabase
                .from('lab_categories')
                .select('id')
                .eq('user_id', userId)
                .eq('name', categoryName)
                .maybeSingle();
              
              if (retryCategory) {
                category = retryCategory;
              } else {
                throw new Error(`Erro ao criar/buscar categoria "${categoryName}": ${newCategoryError.message}`);
              }
            } else {
              throw new Error(`Erro ao criar categoria "${categoryName}": ${newCategoryError.message}`);
            }
          } else {
            category = newCategory;
          }
        } else {
          // Se a categoria existe mas não é do sistema, atualizar para is_system = true
          // Isso garante consistência (categorias fixas devem sempre ser do sistema)
          if (!category.is_system && (categoryName === 'Dados do paciente' || categoryName === 'Exames' || categoryName === 'Antropometria')) {
            const { error: updateError } = await supabase
              .from('lab_categories')
              .update({ is_system: true })
              .eq('id', category.id);
            
            if (updateError) {
              console.error('[CategorizationSuggestions] Erro ao atualizar is_system:', updateError);
            }
          }
        }

        if (category) {
          categoryMap.set(categoryName, category.id);
        }
      }

      // ✅ PASSO 3: Criar todas as subcategorias de uma vez (batch insert)
      const subcategoriesToInsert = testCategories.map(tc => ({
        category_id: categoryMap.get(tc.categoryName)!,
        user_id: userId,
        name: tc.testName,
        show_chart: true,
        is_auto_created: true,
        slug: '', // Trigger auto-gera do name
      }));

      const { data: newSubcategories, error: subcategoriesError } = await supabase
        .from('lab_subcategories')
        .insert(subcategoriesToInsert)
        .select();

      if (subcategoriesError) throw subcategoriesError;

      if (!newSubcategories || newSubcategories.length === 0) {
        throw new Error('Nenhuma subcategoria foi criada');
      }

      // ✅ PASSO 4: Criar mapa de testName -> subcategoryId
      const subcategoryMap = new Map<string, string>();
      newSubcategories.forEach(sub => {
        subcategoryMap.set(sub.name, sub.id);
      });

      // ✅ PASSO 5: Atualizar lab_results_onboarding em batch (agrupado por source_type)
      const anthropometryTests = testCategories.filter(tc => tc.isAnthropometry).map(tc => tc.testName);
      const labExamTests = testCategories.filter(tc => !tc.isAnthropometry).map(tc => tc.testName);

      // Atualizar antropometria
      if (anthropometryTests.length > 0) {
        for (const testName of anthropometryTests) {
          const subcategoryId = subcategoryMap.get(testName);
          if (subcategoryId) {
            const { error: updateError } = await supabase
              .from('lab_results_onboarding')
              .update({ subcategory_id: subcategoryId })
              .eq('patient_id', patientId)
              .eq('test_name', testName)
              .eq('source_type', 'anthropometry')
              .is('subcategory_id', null);

            if (updateError) {
              console.error(`Erro ao atualizar lab_results para ${testName}:`, updateError);
            }
          }
        }
      }

      // Atualizar lab_exam
      if (labExamTests.length > 0) {
        for (const testName of labExamTests) {
          const subcategoryId = subcategoryMap.get(testName);
          if (subcategoryId) {
            const { error: updateError } = await supabase
              .from('lab_results_onboarding')
              .update({ subcategory_id: subcategoryId })
              .eq('patient_id', patientId)
              .eq('test_name', testName)
              .is('subcategory_id', null);

            if (updateError) {
              console.error(`Erro ao atualizar lab_results para ${testName}:`, updateError);
            }
          }
        }
      }

      // ✅ PASSO 6: Atualizar structured_records da sessão
      const { data: session, error: sessionError } = await supabase
        .from('smartfile_sessions')
        .select('structured_records')
        .eq('id', sessionId)
        .single();

      if (!sessionError && session?.structured_records && Array.isArray(session.structured_records)) {
        // Helper para mapear test_name de volta para measurement_type_slug
        const mapTestNameToMeasurementTypeSlug = (testName: string): string[] => {
          const mapping: Record<string, string[]> = {
            'Peso': ['peso', 'weight'],
            'IMC': ['imc', 'bmi'],
            'Altura': ['altura', 'height'],
            'Circunferência da Cintura': ['circunferencia_cintura', 'circunferencia_cintura', 'circunferenciacintura', 'cintura', 'waist'],
            'Circunferência do Quadril': ['circunferencia_quadril', 'circunferencia_quadril', 'circunferenciaquadril', 'quadril', 'hip'],
            'Gordura Corporal': ['gordura_corporal', 'gordura_corporal', 'gorduracorporal', 'body_fat'],
            'Massa Magra': ['massa_magra', 'massa_magra', 'massamagra', 'lean_mass'],
            'RCQ': ['rcq', 'waist_hip_ratio', 'whtr']
          };
          return mapping[testName] || [testName.toLowerCase().replace(/\s+/g, '_').normalize('NFD').replace(/[\u0300-\u036f]/g, '')];
        };

        let updated = false;
        const updatedRecords = (session.structured_records as StructuredRecord[]).map((record) => {
          // Verificar se é antropometria e se precisa atualizar
          if (
            (record?.type === 'anthropometry' || 
             (record?.type === 'physiotherapy_evolution' && record?.structure_type === 'antropometry')) &&
            record?.measurement_type_slug
          ) {
            // Verificar se algum dos testNames criados corresponde a este registro
            for (const testName of anthropometryTests) {
              const subcategoryId = subcategoryMap.get(testName);
              if (subcategoryId) {
                const measurementTypeSlugs = mapTestNameToMeasurementTypeSlug(testName);
                if (measurementTypeSlugs.includes(record.measurement_type_slug.toLowerCase())) {
                  updated = true;
                  return {
                    ...record,
                    subcategory_id: subcategoryId
                  };
                }
              }
            }
          }
          return record;
        });

        if (updated) {
          const { error: updateStructuredError } = await supabase
            .from('smartfile_sessions')
            .update({ structured_records: updatedRecords as any as typeof session.structured_records })
            .eq('id', sessionId);

          if (updateStructuredError) {
            console.error('Error updating structured_records:', updateStructuredError);
          }
        }
      }

      toast.success(`🎉 ${newSubcategories.length} subcategoria(s) criada(s)!`);

      // Limpar sugestões
      setSuggestedTests([]);

      // Callback to reload session data
      await onSubcategoriesCreated();
      
      // Disparar evento para recarregar gráficos nos pacientes
      window.dispatchEvent(new CustomEvent('reload-health-indicators'));

    } catch (error) {
      console.error('Error creating all subcategories:', error);
      toast.error('Erro ao criar subcategorias: ' + (error as Error).message);
    } finally {
      setCreatingAll(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
        <CardContent className="py-6">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
            <p className="text-sm text-muted-foreground">Detectando novas medidas...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't render if no suggestions
  if (suggestedTests.length === 0) {
    return null;
  }

  return (
    <Card className="border-2 border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
            <TrendingUp className="h-6 w-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg">Categorização e Gráficos</CardTitle>
            <CardDescription>
              Novas medidas encontradas • Crie subcategorias para acompanhar no perfil do paciente
            </CardDescription>
          </div>
          <Badge variant="secondary" className="text-sm">
            {suggestedTests.length} nova(s)
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Explanatory message */}
        <Alert className="bg-purple-100/50 border-purple-300">
          <Lightbulb className="h-4 w-4 text-purple-600" />
          <AlertDescription>
            Ao criar essas subcategorias, você poderá visualizar a evolução dessas medidas 
            através de <strong>gráficos interativos</strong> no perfil do paciente.
          </AlertDescription>
        </Alert>

        {/* List of suggested test names */}
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {suggestedTests.map((test, idx) => (
            <div 
              key={idx} 
              className="flex items-center justify-between p-3 rounded-lg border bg-white dark:bg-gray-900"
            >
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="font-medium">{test.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {test.count} resultado(s) encontrado(s)
                  </p>
                </div>
              </div>
              
              <Button 
                size="sm" 
                onClick={() => handleCreateSubcategory(test.name)}
                disabled={creating || creatingAll}
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Criar
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Button to create all at once */}
        {suggestedTests.length > 1 && (
          <Button 
            className="w-full" 
            variant="default"
            onClick={handleCreateAllSubcategories}
            disabled={creating || creatingAll}
          >
            {creatingAll ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Criando subcategorias...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Criar Todas as Subcategorias ({suggestedTests.length})
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
